import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';
import { Download } from '@mui/icons-material';

const COLOR_PALETTE = [
  "#4f46e5",
  "#0d9488",
  "#d97706",
  "#2563eb",
  "#e11d48",
  "#65a30d",
  "#7c3aed",
  "#0891b2",
  "#c026d3",
  "#16a34a"
];

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Determine readable text color given background alpha on white page
const getTextColor = (hex, alpha) => {
  // Blend hex color with white at given alpha → compute luminance
  const r = (parseInt(hex.slice(1, 3), 16) * alpha + 255 * (1 - alpha)) / 255;
  const g = (parseInt(hex.slice(3, 5), 16) * alpha + 255 * (1 - alpha)) / 255;
  const b = (parseInt(hex.slice(5, 7), 16) * alpha + 255 * (1 - alpha)) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45 ? '#1e293b' : '#ffffff';
};

export default function HeatmapChart({ title, tooltip, data, onDownload, onClick, loading, minCell = 52, maxCell = 90, stretch = false, showColTotals = false, showHeader = true, conditionalRules = [], valColumn = 'total' }) {
  const [hoveredCol, setHoveredCol] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [cellWidth, setCellWidth] = useState(44);
  const containerRef = useRef(null);
  const ROLE_LABEL_WIDTH = 130;
  const CELL_GAP = 3;
  const MIN_CELL = minCell;
  const MAX_CELL = maxCell;

  const { maxVal, gridData, validLabels } = useMemo(() => {
    if (!data || !data.datasets || !data.labels) return { maxVal: 0, gridData: [], validLabels: [] };

    const validIndices = [];
    const vLabels = [];
    data.labels.forEach((lbl, i) => {
      if (lbl && String(lbl).trim() !== '') {
        validIndices.push(i);
        vLabels.push(lbl);
      }
    });

    let m = 0;
    const grid = data.datasets.map((ds, rIdx) => ({
      role: ds.label,
      color: COLOR_PALETTE[rIdx % COLOR_PALETTE.length],
      cells: validIndices.map((origIdx, cIdx) => {
        const item = ds.data[origIdx];
        let val = 0;
        let userCount = 0;
        
        if (typeof item === 'object' && item !== null) {
             val = item.val !== undefined ? item.val : (item.count !== undefined ? item.count : 0);
             userCount = item.userCount !== undefined ? item.userCount : 0;
        } else {
             val = typeof item === 'number' || typeof item === 'string' ? Number(item) : 0;
        }
        
        // Failsafe primitive casting
        val = typeof val === 'object' && val !== null ? (val.count || val.val || 0) : Number(val);
        userCount = typeof userCount === 'object' && userCount !== null ? (userCount.userCount || 0) : Number(userCount);
        val = isNaN(val) ? 0 : val;
        userCount = isNaN(userCount) ? 0 : userCount;

        if (val > m) m = val;
        return { state: vLabels[cIdx], val, userCount };
      })
    }));
    return { maxVal: m, gridData: grid, validLabels: vLabels };
  }, [data]);

  // Column totals (sum of all roles per state/month)
  const colTotals = useMemo(() => {
    if (!showColTotals || !gridData.length) return [];
    const numCols = gridData[0]?.cells?.length || 0;
    return Array.from({ length: numCols }, (_, cIdx) => {
      return gridData.reduce((acc, row) => {
        let cellVal = row.cells[cIdx].val;
        let cellUserCount = row.cells[cIdx].userCount;
        
        cellVal = typeof cellVal === 'object' && cellVal !== null ? (cellVal.count || cellVal.val || 0) : Number(cellVal);
        cellUserCount = typeof cellUserCount === 'object' && cellUserCount !== null ? (cellUserCount.userCount || 0) : Number(cellUserCount);

        acc.val += (isNaN(cellVal) ? 0 : cellVal);
        acc.userCount += (isNaN(cellUserCount) ? 0 : cellUserCount);
        return acc;
      }, { val: 0, userCount: 0 });
    });
  }, [gridData, showColTotals]);

  const maxColTotal = useMemo(() => Math.max(...colTotals.map(t => t.val), 0), [colTotals]);

  // Recalculate cell width whenever container resizes or data changes
  useEffect(() => {
    if (!containerRef.current) return;
    const numCols = validLabels.length;
    if (numCols === 0) return;

    const calc = () => {
      const containerWidth = containerRef.current?.offsetWidth || 800;
      const available = containerWidth - ROLE_LABEL_WIDTH - 32; // 32 = padding
      const rawCell = Math.floor((available - (numCols - 1) * CELL_GAP) / numCols);
      setCellWidth(Math.min(MAX_CELL, Math.max(MIN_CELL, rawCell)));
    };

    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [validLabels.length]);

  const handleMouseEnter = useCallback((e, cIdx, cell, role, color) => {
    setHoveredCol(cIdx);
    setTooltipData({ x: e.clientX, y: e.clientY, state: cell.state, role, val: cell.val, userCount: cell.userCount, color });
  }, []);

  const handleMouseMove = useCallback((e, cIdx, cell, role, color) => {
    setTooltipData(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredCol(null);
    setTooltipData(null);
  }, []);

  const displayTitle = title || "Heatmap Chart";

  if (loading || !data) {
    return (
      <Box ref={containerRef} p={3} sx={{ position: 'relative', width: '100%', userSelect: 'none' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary" textTransform="uppercase" letterSpacing={1} fontSize="0.78rem">
              {displayTitle}
            </Typography>
          </Box>
          {onDownload && (
            <IconButton onClick={onDownload} size="small" sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Download fontSize="small" />
            </IconButton>
          )}
        </Box>
        {/* Skeleton Grid */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, width: '100%', mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Skeleton variant="rectangular" width={ROLE_LABEL_WIDTH} height={20} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Skeleton variant="rectangular" width="80%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Skeleton variant="rectangular" width={ROLE_LABEL_WIDTH} height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Skeleton variant="rectangular" width="80%" height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Skeleton variant="rectangular" width={ROLE_LABEL_WIDTH} height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Skeleton variant="rectangular" width="80%" height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Skeleton variant="rectangular" width={ROLE_LABEL_WIDTH} height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Skeleton variant="rectangular" width="80%" height={38} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} p={showHeader ? 3 : 0} sx={{ position: 'relative', width: '100%', userSelect: 'none' }}>
      {/* Header */}
      {showHeader && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary" textTransform="uppercase" letterSpacing={1} fontSize="0.78rem">
              {title}
            </Typography>

          </Box>
          {onDownload && (
            <IconButton onClick={onDownload} size="small" sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Download fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Grid container */}
      <Box sx={{ overflowX: stretch ? 'hidden' : 'auto', pb: 2, width: '100%' }}>

        {/* Header row — sticky corner + column labels */}
        <Box display="flex" mb="8px" sx={stretch ? { width: '100%' } : { minWidth: 'max-content' }}>
          {/* Sticky white corner covering role label column */}
          <Box sx={{
            width: ROLE_LABEL_WIDTH,
            flexShrink: 0,
            position: 'sticky',
            left: 0,
            zIndex: 7,
            bgcolor: 'white',
          }} />
          {/* State / month column headers */}
          {validLabels.map((state, i) => (
            <Box
              key={i}
              sx={{
                // stretch mode: flex:1 fills available space. fixed mode: use computed cellWidth
                ...(stretch ? { flex: 1, minWidth: 0 } : { width: cellWidth, flexShrink: 0 }),
                mx: `${CELL_GAP / 2}px`,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pb: '4px',
                height: 80,
                overflow: 'hidden',
              }}
            >
              <Typography sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontSize: '0.68rem',
                color: hoveredCol === i ? '#1e293b' : 'text.secondary',
                fontWeight: hoveredCol === i ? 800 : 400,
                whiteSpace: 'nowrap',
                textAlign: 'center',
                transition: 'all 0.15s',
                lineHeight: 1,
              }}>
                {state}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Grid rows */}
        {gridData.map((row, rIdx) => (
          <Box key={rIdx} display="flex" alignItems="center" mb="3px" sx={stretch ? { width: '100%' } : { minWidth: 'max-content' }}>
            {/* Role label — sticky left column */}
            <Box sx={{
              width: ROLE_LABEL_WIDTH,
              pr: 2,
              textAlign: 'right',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'text.secondary',
              flexShrink: 0,
              position: 'sticky',
              left: 0,
              zIndex: 5,
              bgcolor: 'white',
              py: '2px',
              // Right shadow to visually separate from scrolling cells
              boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
            }}>
              {row.role}
            </Box>

            {/* Cells — stretch fills remaining space, fixed uses computed cellWidth */}
            <Box display="flex" gap={`${CELL_GAP}px`} sx={stretch ? { flex: 1, minWidth: 0 } : {}}>
              {row.cells.map((cell, cIdx) => {
                const isEmpty = cell.val === 0;
                const intensity = maxVal > 0 ? cell.val / maxVal : 0;
                const alpha = isEmpty ? 0.07 : 0.18 + intensity * 0.82;
                const colHighlighted = hoveredCol === cIdx;
                const displayAlpha = colHighlighted ? Math.min(alpha + 0.15, 1) : alpha;

                let customBg = null;
                let customText = null;
                if (!isEmpty && conditionalRules && conditionalRules.length > 0 && valColumn) {
                    const matchedRule = conditionalRules.find(r => r.column === valColumn);
                    if (matchedRule) {
                        const target = Number(matchedRule.target);
                        if (!isNaN(target)) {
                            let matches = false;
                            const value = Number(cell.val);
                            if (matchedRule.operator === '>') matches = value > target;
                            else if (matchedRule.operator === '<') matches = value < target;
                            else if (matchedRule.operator === '=') matches = value === target;

                            if (matches) {
                                customBg = matchedRule.bgColor;
                                customText = matchedRule.textColor;
                            }
                        }
                    }
                }

                const textColor = customText || getTextColor(row.color, displayAlpha);

                return (
                  <Box
                    key={cIdx}
                    onClick={() => !isEmpty && onClick && onClick(cell.state, row.role)}
                    onMouseEnter={(e) => handleMouseEnter(e, cIdx, cell, row.role, row.color)}
                    onMouseMove={(e) => handleMouseMove(e, cIdx, cell, row.role, row.color)}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      // stretch: each cell shares space equally; fixed: use computed cellWidth
                      ...(stretch
                        ? { flex: 1, minWidth: 0 }
                        : { width: cellWidth, flexShrink: 0 }
                      ),
                      height: Math.max(38, Math.round((stretch ? 56 : cellWidth) * 0.65)),
                      bgcolor: customBg || hexToRgba(row.color, displayAlpha),
                      borderRadius: '5px',
                      cursor: (!isEmpty && onClick) ? 'pointer' : 'default',
                      border: colHighlighted
                        ? `2px solid ${row.color}`
                        : `1px solid ${hexToRgba(row.color, isEmpty ? 0.12 : 0.28)}`,
                      transform: colHighlighted ? 'scaleY(1.08)' : 'scaleY(1)',
                      zIndex: colHighlighted ? 4 : 1,
                      transition: 'all 0.15s ease',
                      boxShadow: colHighlighted ? `0 2px 10px ${hexToRgba(row.color, 0.4)}` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {!isEmpty && (
                      <Box display="flex" flexDirection="column" alignItems="center" sx={{ pointerEvents: 'none' }}>
                        <Typography sx={{
                          fontSize: cell.val >= 100 ? '0.6rem' : '0.68rem',
                          fontWeight: 800,
                          color: textColor,
                          lineHeight: 1.1,
                          letterSpacing: '-0.3px',
                        }}>
                          {cell.val}
                        </Typography>
                        {cell.userCount > 0 && (
                          <Typography sx={{
                            fontSize: '0.55rem',
                            fontWeight: 600,
                            color: textColor,
                            lineHeight: 1.1,
                            opacity: 0.85,
                          }}>
                            ({cell.userCount})
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

        {/* TOTAL row — sum of all roles per column */}
        {showColTotals && colTotals.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            mt="6px"
            sx={stretch ? { width: '100%' } : { minWidth: 'max-content' }}
          >
            {/* Sticky TOTAL label */}
            <Box sx={{
              width: ROLE_LABEL_WIDTH,
              pr: 2,
              textAlign: 'right',
              fontSize: '0.7rem',
              fontWeight: 800,
              color: '#1e293b',
              flexShrink: 0,
              position: 'sticky',
              left: 0,
              zIndex: 5,
              bgcolor: 'white',
              py: '2px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
            }}>
              TOTAL
            </Box>

            {/* Total cells */}
            <Box display="flex" gap={`${CELL_GAP}px`} sx={stretch ? { flex: 1, minWidth: 0 } : {}}>
              {colTotals.map((totalData, cIdx) => {
                const { val: total, userCount } = totalData;
                const intensity = maxColTotal > 0 ? total / maxColTotal : 0;
                const colHighlighted = hoveredCol === cIdx;
                const alpha = total === 0 ? 0.06 : 0.25 + intensity * 0.75;
                const displayAlpha = colHighlighted ? Math.min(alpha + 0.1, 1) : alpha;
                const state = validLabels[cIdx];

                return (
                  <Box
                    key={cIdx}
                    onMouseEnter={(e) => handleMouseEnter(e, cIdx, { state, val: total, userCount }, 'All Roles', '#334155')}
                    onMouseMove={(e) => handleMouseMove(e, cIdx, { state, val: total, userCount }, 'All Roles', '#334155')}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      ...(stretch ? { flex: 1, minWidth: 0 } : { width: cellWidth, flexShrink: 0 }),
                      height: Math.max(38, Math.round((stretch ? 56 : cellWidth) * 0.65)),
                      bgcolor: hexToRgba('#334155', displayAlpha),
                      borderRadius: '5px',
                      border: colHighlighted
                        ? '2px solid #334155'
                        : `1px solid ${hexToRgba('#334155', 0.25)}`,
                      transform: colHighlighted ? 'scaleY(1.08)' : 'scaleY(1)',
                      zIndex: colHighlighted ? 4 : 1,
                      transition: 'all 0.15s ease',
                      boxShadow: colHighlighted ? '0 2px 10px rgba(51,65,85,0.4)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {total > 0 && (
                      <Box display="flex" flexDirection="column" alignItems="center" sx={{ pointerEvents: 'none' }}>
                        <Typography sx={{
                          fontSize: total >= 1000 ? '0.55rem' : total >= 100 ? '0.6rem' : '0.68rem',
                          fontWeight: 800,
                          color: displayAlpha > 0.5 ? '#fff' : '#1e293b',
                          lineHeight: 1.1,
                          letterSpacing: '-0.3px',
                        }}>
                          {total}
                        </Typography>
                        {userCount > 0 && (
                          <Typography sx={{
                            fontSize: '0.55rem',
                            fontWeight: 600,
                            color: displayAlpha > 0.5 ? '#fff' : '#1e293b',
                            lineHeight: 1.1,
                            opacity: 0.85,
                          }}>
                            ({userCount})
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Floating tooltip */}
      {tooltipData && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltipData.x + 18,
            top: tooltipData.y + 14,
            bgcolor: '#1e293b',
            borderRadius: '10px',
            px: 2,
            py: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
            minWidth: 160,
            zIndex: 99999,
            pointerEvents: 'none',
            borderLeft: `4px solid ${tooltipData.color}`,
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', mb: '2px' }}>
            {tooltipData.state}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mb: '6px' }}>
            {tooltipData.role}
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: tooltipData.val === 0 ? '#64748b' : '#f8fafc' }}>
            {tooltipData.val}
            <Typography component="span" sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 400, ml: 0.5 }}>
              {title?.toLowerCase().includes('login') ? 'logins' : 'quotations'}
            </Typography>
          </Typography>
          {tooltipData.userCount > 0 && (
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#94a3b8', mt: '2px' }}>
              {tooltipData.userCount} users
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
